import type {
  BrandOverrides,
  ButtonOptions,
  DonationContext,
  Frequency,
  PopupTheme,
  RecurringFrequency,
  UtmParam,
} from './types'

/** Decoded shape of the popup URL query string. The SDK serializes a
 *  ButtonOptions into these param names; the popup parses them back into
 *  the same shape. Adding a param requires one edit here, not two. */
export interface PopupQuery {
  pk: string
  env?: 'live' | 'sandbox'
  parent_origin?: string

  amount: number
  currency?: string
  frequency: Frequency
  email?: string

  organization_brand_id?: number
  designation_id?: number
  campaign_id?: number
  campaign_fundraiser_id?: number
  fundraiser_id?: number
  checkout_id?: number
  campaign_event_id?: number
  message_id?: string
  message_donor_id?: string

  tracking_url?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  utm_id?: string

  cover_fees?: boolean

  recurring_frequency?: RecurringFrequency
  recurring_start_date?: string
  recurring_end_date?: string

  brand_color?: string
  brand_fg?: string
  brand_radius?: string
  brand_font?: string

  /** Pass-through brand fields the popup may use to skin chrome before the
   *  PK session resolves. Not part of the SDK's brand options today; reserved
   *  for the popup's existing `logo` / `brand_name` query params. */
  logo?: string
  brand_name?: string

  /** Decoded full theme — `serializePopupQuery` encodes the embedder's
   *  PopupTheme as a single base64url JSON `theme` param so the URL stays
   *  compact and the schema can grow without param bloat. */
  theme?: PopupTheme
}

export const UTM_PARAMS: readonly UtmParam[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
] as const

interface SerializeOptions extends ButtonOptions {
  publishableKey: string
  environment?: 'live' | 'sandbox'
  parentOrigin?: string
}

/** Serialize the SDK's ButtonOptions (plus PK / env / parent_origin from the
 *  WeGivePay config) into the popup's URL query string. */
export function serializePopupQuery(opts: SerializeOptions): URLSearchParams {
  const params = new URLSearchParams()
  params.set('pk', opts.publishableKey)
  params.set('env', opts.environment ?? 'live')
  params.set('amount', String(opts.amount))
  if (opts.currency) params.set('currency', opts.currency.toUpperCase())
  params.set('frequency', opts.frequency ?? 'once')
  if (opts.parentOrigin) params.set('parent_origin', opts.parentOrigin)
  if (opts.email) params.set('email', opts.email)

  setNumeric(params, 'organization_brand_id', opts.organizationBrandId)
  setNumeric(params, 'designation_id', opts.designationId)
  setNumeric(params, 'campaign_id', opts.campaignId)
  setNumeric(params, 'campaign_fundraiser_id', opts.campaignFundraiserId)
  setNumeric(params, 'fundraiser_id', opts.fundraiserId)
  setNumeric(params, 'checkout_id', opts.checkoutId)
  setNumeric(params, 'campaign_event_id', opts.campaignEventId)
  if (opts.messageId) params.set('message_id', opts.messageId)
  if (opts.messageDonorId) params.set('message_donor_id', opts.messageDonorId)

  if (opts.trackingUrl) params.set('tracking_url', opts.trackingUrl)
  for (const key of UTM_PARAMS) {
    const value = opts.utmParams?.[key]
    if (value) params.set(key, value)
  }

  if (opts.coverFees) params.set('cover_fees', '1')

  if (opts.recurringFrequency) params.set('recurring_frequency', opts.recurringFrequency)
  if (opts.recurringStartDate) params.set('recurring_start_date', opts.recurringStartDate)
  if (opts.recurringEndDate) params.set('recurring_end_date', opts.recurringEndDate)

  if (opts.brand?.color) params.set('brand_color', opts.brand.color)
  if (opts.brand?.foreground) params.set('brand_fg', opts.brand.foreground)
  if (opts.brand?.radius) params.set('brand_radius', opts.brand.radius)
  if (opts.brand?.font) params.set('brand_font', opts.brand.font)

  // Full popup theme — packed as base64url JSON in a single `theme` param.
  // `brand: BrandOverrides` is folded in so the popup only has to consume
  // one shape (the legacy brand_* params remain for back-compat parsing).
  const merged = mergeBrandIntoTheme(opts.brand, opts.theme)
  if (merged) params.set('theme', encodeTheme(merged))

  return params
}

/** Parse a popup URL query string into the typed PopupQuery + a partial
 *  DonationContext the popup checkout store can hydrate from. */
export function parsePopupQuery(params: URLSearchParams): PopupQuery {
  return {
    pk: params.get('pk') ?? '',
    env: (params.get('env') as 'live' | 'sandbox' | null) ?? undefined,
    parent_origin: params.get('parent_origin') ?? undefined,

    amount: Number(params.get('amount') ?? 0),
    currency: params.get('currency')?.toUpperCase() ?? undefined,
    frequency: (params.get('frequency') as Frequency | null) ?? 'once',
    email: params.get('email') ?? undefined,

    organization_brand_id: numOrUndefined(params.get('organization_brand_id')),
    designation_id: numOrUndefined(params.get('designation_id')),
    campaign_id: numOrUndefined(params.get('campaign_id')),
    campaign_fundraiser_id: numOrUndefined(params.get('campaign_fundraiser_id')),
    fundraiser_id: numOrUndefined(params.get('fundraiser_id')),
    checkout_id: numOrUndefined(params.get('checkout_id')),
    campaign_event_id: numOrUndefined(params.get('campaign_event_id')),
    message_id: params.get('message_id') ?? undefined,
    message_donor_id: params.get('message_donor_id') ?? undefined,

    tracking_url: params.get('tracking_url') ?? undefined,
    utm_source: params.get('utm_source') ?? undefined,
    utm_medium: params.get('utm_medium') ?? undefined,
    utm_campaign: params.get('utm_campaign') ?? undefined,
    utm_term: params.get('utm_term') ?? undefined,
    utm_content: params.get('utm_content') ?? undefined,
    utm_id: params.get('utm_id') ?? undefined,

    cover_fees: params.get('cover_fees') === '1',

    recurring_frequency:
      (params.get('recurring_frequency') as RecurringFrequency | null) ?? undefined,
    recurring_start_date: params.get('recurring_start_date') ?? undefined,
    recurring_end_date: params.get('recurring_end_date') ?? undefined,

    brand_color: params.get('brand_color') ?? undefined,
    brand_fg: params.get('brand_fg') ?? undefined,
    brand_radius: params.get('brand_radius') ?? undefined,
    brand_font: params.get('brand_font') ?? undefined,

    logo: params.get('logo') ?? undefined,
    brand_name: params.get('brand_name') ?? undefined,

    theme: parseThemeParam(params),
  }
}

/** Parse the `theme` param, then fold the legacy `brand_*` params on top so
 *  embedders mixing old + new shapes get a consistent result. */
function parseThemeParam(params: URLSearchParams): PopupTheme | undefined {
  const encoded = params.get('theme')
  let theme: PopupTheme | undefined = encoded ? decodeTheme(encoded) : undefined

  const brandColor = params.get('brand_color') ?? undefined
  const brandFg = params.get('brand_fg') ?? undefined
  const brandRadius = params.get('brand_radius') ?? undefined
  const brandFont = params.get('brand_font') ?? undefined
  const hasLegacy = !!(brandColor || brandFg || brandRadius || brandFont)
  if (!hasLegacy) return theme

  const legacy: BrandOverrides = {
    ...(brandColor ? { color: brandColor } : {}),
    ...(brandFg ? { foreground: brandFg } : {}),
    ...(brandRadius ? { radius: brandRadius } : {}),
    ...(brandFont ? { font: brandFont } : {}),
  }
  return mergeBrandIntoTheme(legacy, theme)
}

/** Translate `BrandOverrides` (the convenience shape) into the canonical
 *  PopupTheme shape: shared radius/font, brand color applied to both light
 *  and dark surfaces. Returns the merged theme, or undefined if neither
 *  source supplied any values. */
export function mergeBrandIntoTheme(
  brand: BrandOverrides | undefined,
  theme: PopupTheme | undefined,
): PopupTheme | undefined {
  if (!brand && !theme) return undefined
  if (!brand) return theme
  const fromBrand: PopupTheme = {}
  if (brand.radius) fromBrand.radius = brand.radius
  if (brand.font) fromBrand.font = brand.font
  if (brand.color || brand.foreground) {
    const tokens: PopupTheme['light'] = {}
    if (brand.color) tokens.brand = brand.color
    if (brand.foreground) tokens.brandFg = brand.foreground
    fromBrand.light = tokens
    fromBrand.dark = tokens
  }
  if (!theme) return fromBrand
  // Theme wins on conflicting fields — embedders who pass both are saying
  // "use the structured theme; brand is a fallback for the simple case".
  return {
    ...fromBrand,
    ...theme,
    light: { ...fromBrand.light, ...theme.light },
    dark: { ...fromBrand.dark, ...theme.dark },
  }
}

/** Base64url-encode a PopupTheme as a URL-safe single query param. */
export function encodeTheme(theme: PopupTheme): string {
  const json = JSON.stringify(theme)
  // utf-8 safe base64 → base64url.
  const utf8 = typeof TextEncoder !== 'undefined'
    ? new TextEncoder().encode(json)
    : null
  let b64: string
  if (utf8) {
    let binary = ''
    for (let i = 0; i < utf8.length; i++) binary += String.fromCharCode(utf8[i])
    b64 = btoa(binary)
  } else {
    b64 = btoa(unescape(encodeURIComponent(json)))
  }
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Inverse of `encodeTheme`. Returns undefined on malformed input — the
 *  popup falls back to defaults rather than refusing to render. */
export function decodeTheme(value: string): PopupTheme | undefined {
  try {
    let b64 = value.replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4
    if (pad) b64 += '='.repeat(4 - pad)
    const binary = atob(b64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    const json = typeof TextDecoder !== 'undefined'
      ? new TextDecoder().decode(bytes)
      : decodeURIComponent(escape(binary))
    const parsed = JSON.parse(json)
    return parsed && typeof parsed === 'object' ? (parsed as PopupTheme) : undefined
  } catch {
    return undefined
  }
}

/** Lift the parsed PopupQuery into a DonationContext + UTM payload, which
 *  the popup's checkout store keeps as its canonical input. */
export function donationContextFromQuery(query: PopupQuery): DonationContext {
  const utmParams: Partial<Record<UtmParam, string>> = {}
  for (const key of UTM_PARAMS) {
    const value = query[key]
    if (value) utmParams[key] = value
  }
  return {
    amount: query.amount,
    currency: query.currency,
    frequency: query.frequency,
    email: query.email,
    organizationBrandId: query.organization_brand_id,
    designationId: query.designation_id,
    campaignId: query.campaign_id,
    campaignFundraiserId: query.campaign_fundraiser_id,
    fundraiserId: query.fundraiser_id,
    checkoutId: query.checkout_id,
    campaignEventId: query.campaign_event_id,
    messageId: query.message_id,
    messageDonorId: query.message_donor_id,
    trackingUrl: query.tracking_url,
    utmParams: Object.keys(utmParams).length ? utmParams : undefined,
    coverFees: query.cover_fees,
    recurringFrequency: query.recurring_frequency,
    recurringStartDate: query.recurring_start_date,
    recurringEndDate: query.recurring_end_date,
  }
}

function setNumeric(params: URLSearchParams, key: string, value: number | undefined) {
  if (value != null) params.set(key, String(value))
}

function numOrUndefined(v: string | null): number | undefined {
  if (v == null || v === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}
